import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import firebase from 'firebase/compat/app';

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InquiryModal: React.FC<InquiryModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string, isPermission: boolean }>({ message: '', isPermission: false });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    solution: 'OpusGo'
  });

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setError({ message: '', isPermission: false });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError({ message: '', isPermission: false });

    try {
      // 1. SILENT AUTH ATTEMPT (Optional)
      try {
        if (!auth.currentUser) {
          await auth.signInAnonymously();
        }
      } catch (authErr) {
        console.warn("Anonymous auth restricted or disabled. Proceeding with public write attempt.");
      }

      // 2. FIRESTORE WRITE
      await db.collection('inquiries').add({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        solution: formData.solution,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        source: 'Landing Page Modal',
        platform: window.location.hostname
      });

      setLoading(false);
      setStep('success');
    } catch (err: any) {
      console.error('Submission Error:', err);

      if (err.code === 'permission-denied') {
        setError({
          message: 'Permission Denied: Your Firestore Security Rules are blocking this write.',
          isPermission: true
        });
      } else {
        setError({
          message: err.message || 'An unexpected error occurred. Please try again.',
          isPermission: false
        });
      }

      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6">
      <div
        className="absolute inset-0 bg-[#021226]/80 backdrop-blur-xl transition-opacity animate-reveal"
        onClick={onClose}
      />

      <div className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-reveal border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-slate-50 text-slate-400 hover:text-gray-900 transition-colors z-20"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div className="flex flex-col md:flex-row h-full">
          <div className="hidden md:flex md:w-1/3 bg-[#021226] p-8 flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[#FF5C00]/5 rounded-full blur-[60px] -translate-x-1/2"></div>
            <div className="relative z-10">
              <img src="https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OVL+-+NewLogo.webp" alt="OVL" className="h-10 mb-8" />
              <h3 className="text-white text-xl font-black leading-tight tracking-tight">
                Transform Your <br />Retail Experience.
              </h3>
            </div>
            <div className="relative z-10">
              <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Global Support</p>
              <p className="text-white text-xs font-medium mt-1">+91 99025 94100</p>
            </div>
          </div>

          <div className="flex-1 p-8 md:p-12">
            {step === 'form' ? (
              <div className="animate-reveal">
                <div className="mb-8">
                  <h2 className="text-2xl font-black text-[#021226] tracking-tight mb-2">Book Your Live Demo</h2>
                  <p className="text-sm text-slate-500 font-medium">Quick inquiry: Tell us who you are and what you're interested in.</p>
                </div>

                {error.message && (
                  <div className="mb-6 space-y-4">
                    <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-xs font-bold flex gap-3">
                      <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                      <span>{error.message}</span>
                    </div>

                    {error.isPermission && (
                      <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800 animate-reveal">
                        <p className="text-[10px] font-black text-[#FF5C00] uppercase tracking-widest mb-3">Fix: Update Firebase Rules</p>
                        <pre className="text-[9px] font-mono text-slate-300 overflow-x-auto p-2 bg-black/30 rounded-lg">
                          {`match /inquiries/{docId} {
  allow write: if true;
}`}
                        </pre>
                        <p className="text-[9px] text-slate-500 mt-3 italic">Paste this in your Firebase Console &gt; Firestore &gt; Rules tab.</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 mb-6 flex items-center gap-3">
                  <span className="text-[#FF5C00]">🔥</span>
                  <p className="text-xs font-bold text-orange-800">
                    Limited slots available this week. Average response time: 2 hours.
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Full Name</label>
                      <input
                        required
                        type="text"
                        placeholder="John Doe"
                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-[#FF5C00]/5 focus:border-[#FF5C00] outline-none transition-all"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Business Email</label>
                      <input
                        required
                        type="email"
                        placeholder="john@company.com"
                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-[#FF5C00]/5 focus:border-[#FF5C00] outline-none transition-all"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Phone Number</label>
                      <input
                        required
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-[#FF5C00]/5 focus:border-[#FF5C00] outline-none transition-all"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Solution Interest</label>
                      <div className="relative">
                        <select
                          className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-[#021226] focus:ring-4 focus:ring-[#FF5C00]/5 focus:border-[#FF5C00] outline-none transition-all appearance-none cursor-pointer"
                          value={formData.solution}
                          onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                        >
                          <option value="OpusGo">OpusGo (Retail)</option>
                          <option value="Opus3D">Opus3D (Enterprise)</option>
                          <option value="OpusStudio">OpusStudio (Designers)</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    disabled={loading}
                    type="submit"
                    className="w-full py-5 bg-[#FF5C00] text-white font-black text-sm uppercase tracking-widest rounded-2xl shadow-xl shadow-[#FF5C00]/20 hover:shadow-[#FF5C00]/40 active:scale-[0.98] transition-all flex items-center justify-center gap-3 mt-4"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        Confirm Booking
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-12 animate-reveal">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 border border-green-100">
                  <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h2 className="text-2xl font-black text-[#021226] tracking-tight mb-2">Request Received!</h2>
                <p className="text-slate-500 text-sm font-medium max-w-xs mb-8">
                  Thank you for your interest, {formData.name.split(' ')[0]}. A solution expert will reach out to you shortly at {formData.phone}.
                </p>
                <button
                  onClick={onClose}
                  className="px-8 py-3.5 bg-[#021226] text-white font-black text-xs uppercase tracking-widest rounded-xl hover:bg-[#003B5C] transition-colors"
                >
                  Return to Site
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;